import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalProducts from "@faire/web-api--source/routes/brand-portal/my-shop/products";
import React, { JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const OpenSizingIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="Open sizing allows you to set minimum order quantities (MOQs) by style, letting customers choose various sizes of a style that contribute towards meeting the MOQ. For example, 2 size small shirts and 4 size medium shirts can meet a minimum order value of 6 shirts."
          description={{
            text: "Introduction to open sizing",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="To edit a product to include open sizing, follow these steps:"
          description={{
            text: "Header for editing product to include open sizing",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Navigate to the <LinkFormatting>Products</LinkFormatting> page and select the product you want to edit. Ensure the product has multiple sizes listed to enable open sizing."
              values={{
                LinkFormatting: ProductsLinkFormatting,
              }}
              description={{
                text: "Step 1 for editing product to include open sizing",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select <BoldFormatting>Open sizing</BoldFormatting> under “How would you like to sell this product?”."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Step 2 for editing product to include open sizing",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Set your Minimum Order Quantity (MOQ), then save the changes. Your listing will now be sold as an open pack."
              description={{
                text: "Step 3 for editing product to include open sizing",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="To edit existing listings, including case packs, follow these steps:"
          description={{
            text: "Header for editing existing listings",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Go to the <BoldFormatting>Bulk Upload</BoldFormatting> tab under <LinkFormatting>Products</LinkFormatting>."
              values={{
                LinkFormatting: ProductsLinkFormatting,
                BoldFormatting,
              }}
              description={{
                text: "Step 1 for editing existing listings",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="<BoldFormatting>Download</BoldFormatting> your existing catalog from the “Adding or editing existing products?” section."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Step 2 for editing existing listings",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="To switch the listing from case packs to open sizing, delete the number under <BoldFormatting>Case Quantity</BoldFormatting> and enter the desired number of units for open sizing under the <BoldFormatting>Minimum Order Quantity</BoldFormatting> section."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Step 3 for editing existing listings",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select <BoldFormatting>Open sizing</BoldFormatting> from the dropdown menu in the <BoldFormatting>Minimum Order Quantity</BoldFormatting> section."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Step 4 for editing existing listings",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={5}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Upload the updated spreadsheet to your portal as a .xlsx file."
              description={{
                text: "Step 5 for editing existing listings",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={6}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select <BoldFormatting>Submit</BoldFormatting>. You’ll receive an email once your product is updated."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Step 6 for editing existing listings",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="For listings including prepacks, you’ll need to set the <BoldFormatting>Option Status</BoldFormatting> for the product to <BoldFormatting>Unpublished</BoldFormatting>. This will deactivate prepacks for that listing but will not affect other options like Sizing or Colors. You’ll receive a confirmation email once updates are complete."
          values={{
            BoldFormatting,
          }}
          description={{
            text: "Instructions for listings including prepacks",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="If you don’t receive this email or encounter error messages when saving these product updates, contact our support team for assistance."
          description={{
            text: "Instructions for contacting support",
          }}
        />
      </Typography>
    </Column>
  );
};

const ProductsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalProducts()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
